<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.code"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="
              validations?.traderCode.required.$invalid
                ? validations.traderCode.required.$message
                : ''
            " />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.name')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.name"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.erpName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.erpName"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.serverAddress')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.serverAddress"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.serverPort')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.serverPort"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.windowsAuthentication')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseToggle
            v-if="mode === 'edit'"
            name="windowsAuthentication"
            v-model="data.windowsAuthentication" />
          <BaseTableCellCheck
            v-else
            :item="data.windowsAuthentication ? data.windowsAuthentication : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.dbName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.dbName"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getWindowsAuthError('dbName')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.dbUsername')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.dbUsername"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getWindowsAuthError('dbUsername')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.dbPassword')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.dbPassword"
            color="base"
            flat
            type="password"
            show-error-as-tooltip
            show-password-toggle
            :error="getWindowsAuthError('dbPassword')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.company')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.companyId ? findCompanyName(data.companyId) : '' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle v-else name="isActive" v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.lastUpdate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.updateUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.updateUserId ? findUserName(data.updateUserId) : '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.createdDate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createUserId ? findUserName(data.createUserId) : '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { isoDateToFullDate } from '@/utils/date';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import { type Representative } from '@/types/Traders';
import type { Validation } from '@vuelidate/core';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';

const data = defineModel<Representative>();
const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  validations?: Validation;
}>();

const lookupsStore = useLookupsStore();
const { users, companies } = storeToRefs(lookupsStore);
const { getUsersLookup, getCompaniesLookup } = lookupsStore;

const { t: $t } = useI18n({ useScope: 'global' });

const windowsAuthErrors = computed(() => {
  if (props.mode !== 'edit' || data.value?.windowsAuthentication === true)
    return { dbName: '', dbUsername: '', dbPassword: '' };

  const requiredMessage = $t('messages.errors.required');
  const hasDbName = !!data.value?.dbName;

  return {
    dbName: '',
    dbUsername: hasDbName && !data.value?.dbUsername ? requiredMessage : '',
    dbPassword: hasDbName && !data.value?.dbPassword ? requiredMessage : '',
  };
});

function getWindowsAuthError(field: 'dbName' | 'dbUsername' | 'dbPassword') {
  return windowsAuthErrors.value[field] || '';
}

onMounted(async () => {
  initDropdowns();
});

async function initDropdowns() {
  await getUsersLookup();
  await getCompaniesLookup();
}

watch(
  () => data.value?.windowsAuthentication,
  (newVal) => {
    if (newVal === true && data.value) {
      data.value.dbUsername = '';
      data.value.dbPassword = '';
    }
  }
);

watch(
  () => data.value?.dbUsername,
  (newVal) => {
    if (newVal && data.value && data.value.windowsAuthentication === true) {
      data.value.windowsAuthentication = false;
    }
  }
);

watch(
  () => data.value?.dbPassword,
  (newVal) => {
    if (newVal && data.value && data.value.windowsAuthentication === true) {
      data.value.windowsAuthentication = false;
    }
  }
);

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);

function findUserName(userId: string): string {
  const user = users.value.find((u) => u.id === userId);
  return user?.name || '-';
}

function findCompanyName(companyId: string): string {
  const company = companies.value.find((u) => u.id === companyId);
  return company?.name || '-';
}
</script>
